import { useEffect, useMemo, useRef, useState } from "react";
import {
  getPolicyHistoryList,
  PolicyHistoryDto,
} from "utils/api/policyHistoryApi";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { formatDateToJSONString } from "utils/shared";
import { serializeFilters } from "../usePremiumReceivableList";

// Used for ReceivedDate only — TransactionDate deliberately bypasses it, see the
// request body below.
//
// ReceivedDate is a plain SQL DATE with no AT TIME ZONE applied on the way out,
// and PolicyHistory_GetListByCriteria treats @ReceivedDate as the *start of a
// day window* rather than an instant. So it needs an explicit offset, but not a
// meaningful one: sent without any marker, System.Text.Json falls back to the
// server's local timezone and what the query matches changes with wherever the
// server happens to run. Hence the trailing Z — formatDateToJSONString's default
// (removeTimezone false) appends it, pinning the value to midnight +00:00.
//
// Two details worth keeping in mind. It only formats real Date objects and
// returns anything else untouched, so handing it the row's string would
// silently ship the raw value; hence the parse. And `new Date` is the same
// local parse `formatDate` uses for the grid cell, so the day filtered on is
// the day the user sees in that row.
const toJsonDateString = (
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDateToJSONString(parsed) as string;
};

// Line items already fetched for a drawer, so collapsing and re-expanding a row
// doesn't refire the request. Owned by TransactionsTable (a ref, cleared
// whenever a new result set lands) rather than this module, so the lifetime is
// tied to the grid's current rows instead of the browser session.
export type PolicyLineItemsCache = Map<string, PolicyHistoryDto[]>;

// Keyed on the criteria that actually determine the response, not on
// row.uniqueIdentifier — StewartTable's getRowInstanceKey exists precisely
// because two rows can share one. Two rows with identical criteria would get
// identical line items, so sharing an entry is correct as well as cheaper. Each
// date is serialized the same way the request body serializes it, so the key
// tracks what actually goes on the wire.
export const buildLineItemsCacheKey = (
  row: PremiumReceivablesTransactionRecord,
  userTimeZone: string
) =>
  [
    userTimeZone,
    row.AgentID,
    row.PolicyYear,
    row.PolicyMonth,
    row.AccountingPeriod,
    toJsonDateString(row.ReceivedDate),
    row.TransactionDate,
  ].join("|");

// A Premium Receivable row is an aggregate over one agent's accounting period;
// this fetches the individual policies behind it for the row's detail drawer.
// Unpaged by design (ListCount 0) — a drawer shows the whole breakdown, and the
// counts involved are per-agent-per-period rather than whole-query.
export function usePolicyLineItems(
  row: PremiumReceivablesTransactionRecord,
  userTimeZone: string,
  cache?: PolicyLineItemsCache
) {
  const {
    AgentID,
    PolicyYear,
    PolicyMonth,
    AccountingPeriod,
    ReceivedDate,
    TransactionDate,
  } = row;

  // Memoized on the criteria primitives rather than `row`, so a parent-list
  // refetch that yields an equivalent row produces the same key and does not
  // refire the request.
  const cacheKey = useMemo(
    () => buildLineItemsCacheKey(row, userTimeZone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userTimeZone,
      AgentID,
      PolicyYear,
      PolicyMonth,
      AccountingPeriod,
      ReceivedDate,
      TransactionDate,
    ]
  );

  // Read the cache during the first render, not in the effect: a hit then
  // paints the policies immediately, with no spinner frame in between. Seeding
  // isLoading on a miss likewise avoids a frame of "No policies found." before
  // the effect gets to flip it.
  const [records, setRecords] = useState<PolicyHistoryDto[]>(
    () => cache?.get(cacheKey) ?? []
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    () => !!userTimeZone && !cache?.get(cacheKey)
  );
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!userTimeZone) return;
    const cached = cache?.get(cacheKey);
    if (cached) {
      // Bump first, so a request still in flight for a previous key is dropped
      // by the stale guard below rather than overwriting the cached records.
      reqIdRef.current++;
      setRecords(cached);
      setIsLoading(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getPolicyHistoryList({
      UserTimeZone: userTimeZone,
      // Set outside serializeFilters so the 0 can't be read as "empty".
      ListCount: 0,
      RequestedPage: 1,
      ...serializeFilters({
        AgentIDList: AgentID,
        PolicyYear,
        PolicyMonth,
        AccountingPeriod,
        ReceivedDate: toJsonDateString(ReceivedDate),
        // Verbatim, deliberately not through toJsonDateString. The list SP
        // returns PR.[TransactionDate] AT TIME ZONE @UserTimeZone, so the row's
        // string already names the right offset (2026-03-13T00:00:00-05:00) —
        // and the criterion is an exact datetimeoffset equality against
        // PH.[ClosedDate], which compares instants. Re-deriving the value would
        // re-stamp it as midnight UTC and match nothing.
        TransactionDate,
      }),
    })
      .then((dtos) => {
        // Stale-response guard, as in usePremiumReceivableList.
        if (reqIdRef.current !== myReq) return;
        // Empties are deliberately not cached: getPolicyHistoryList swallows
        // errors and returns [], so a failed request is indistinguishable from
        // a genuinely empty one. Refetching the rare empty drawer beats
        // memoizing a network failure as "No policies found.".
        if (dtos.length) cache?.set(cacheKey, dtos);
        setRecords(dtos);
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
    // cacheKey already encodes every criterion below; they are listed only
    // because the request body reads them directly.
  }, [
    cacheKey,
    cache,
    userTimeZone,
    AgentID,
    PolicyYear,
    PolicyMonth,
    AccountingPeriod,
    ReceivedDate,
    TransactionDate,
  ]);

  return { records, isLoading };
}

export default usePolicyLineItems;
